import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { LinkAngled } from 'blaze-ui/icons'
import React from 'react'
import { ExternalIntegrationSelect } from './ExternalIntegrationSelect'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCreateOrUpdateScheduledPost } from 'components/ArticlePostPreview/hooks'
import {
  providerFromChannelType,
  useArticlePostPreviewContext,
} from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { SocialChannelType } from 'common/ShareSheetV2/Screens/Default/Default'
import { Button } from 'blaze-ui/Button'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { useOpenIntegrations } from 'hooks/useOpenIntegrations'
import Styles from './BlogIntegrationSelect.module.scss'
import { ButtonSize, ButtonVariant } from 'blaze-ui/Button/Types'

type Props = { className?: string; buttonVariant?: ButtonVariant; renderPlatformIcon?: boolean; size?: ButtonSize }

export const BlogIntegrationSelect = ({
  className,
  buttonVariant = 'tertiary',
  renderPlatformIcon = false,
  size = 'sm',
}: Props): React.ReactElement => {
  const { blogIntegrations } = useBlogIntegrations()
  const { almanacFile } = useArticlePostPreviewContext()
  const { createOrUpdateScheduledPost } = useCreateOrUpdateScheduledPost({
    almanacFile: almanacFile,
    provider: providerFromChannelType[almanacFile.channel as SocialChannelType],
  })
  const { data: scheduledPost } = useScheduledPost({ id: almanacFile.scheduled_post_id })
  const openIntegrations = useOpenIntegrations()

  const primaryProvider = ExternalIntegrationEnum.WordPress

  const updateIntegrationIdImmediately = async (externalIntegrationId: number) => {
    try {
      await createOrUpdateScheduledPost({
        externalIntegrationId,
      })
      showToastMessage('Account changed!', 'success')
    } catch {
      showToastMessage('Failed to change account', 'error')
    }
  }

  const handleConnectToBlog = () => {
    openIntegrations()
  }

  if (blogIntegrations?.length) {
    return (
      <div className={Styles.buttonWrapper}>
        <ExternalIntegrationSelect
          onChange={updateIntegrationIdImmediately}
          size={size}
          primaryProvider={primaryProvider}
          externalIntegrations={blogIntegrations}
          selectedExternalIntegrationId={scheduledPost?.external_integration?.id}
          className={className}
          variant={buttonVariant}
          renderPlatformIcon={renderPlatformIcon}
        />
      </div>
    )
  }

  return (
    <Button variant="secondary" frontIcon={LinkAngled} onPress={handleConnectToBlog} size={size}>
      Connect to a blog
    </Button>
  )
}
